class NkoSliderPager extends HTMLElement {
  async connectedCallback() {
    await customElements.whenDefined("nko-slider");

    const slider = document.getElementById(this.dataset.for);
    if (!this.isConnected || !slider?.vp) {
      return;
    }

    this.slider = slider;
    this.snapPositions = [];
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    this.addEventListener("click", (event) => this.handleClick(event), {
      signal,
    });

    slider.vp.addEventListener("scroll", () => this.scheduleUpdate(), {
      passive: true,
      signal,
    });

    this.resizeObserver = new ResizeObserver(() => this.render());
    this.resizeObserver.observe(slider.vp);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.pendingFrame);
  }

  scheduleUpdate() {
    if (this.pendingFrame) {
      return;
    }

    this.pendingFrame = requestAnimationFrame(() => {
      this.pendingFrame = 0;
      this.updateActiveDot();
    });
  }

  handleClick(event) {
    const dot = event.target.closest(".slider-pager__dot");
    if (!dot) {
      return;
    }

    this.slider.hin(this.snapPositions[Number(dot.dataset.index)]);
  }

  render() {
    const slides = this.slider.slides();
    const slidePositions = slides.map((slide) => this.slider.mitte(slide));

    this.snapPositions = [...new Set(slidePositions)];

    const dotCount =
      this.snapPositions.length > 1 ? this.snapPositions.length : 0;
    if (dotCount !== this.children.length) {
      const dots = this.snapPositions
        .slice(0, dotCount)
        .map((position, index) => {
          const slide = slides[slidePositions.indexOf(position)];
          return this.createDot(
            index,
            slide.dataset.sliderLabel || `${index + 1}`,
          );
        });
      this.replaceChildren(...dots);
    }

    this.updateActiveDot();
  }

  createDot(index, label) {
    const dot = document.createElement("button");
    dot.type = "button";
    dot.className = "slider-pager__dot";
    dot.dataset.index = index;
    dot.setAttribute("aria-label", label);
    return dot;
  }

  updateActiveDot() {
    const dots = [...this.children];
    if (!dots.length) {
      return;
    }

    const activeIndex = this.findActiveIndex();
    dots.forEach((dot, index) => {
      if (index === activeIndex) {
        dot.setAttribute("aria-current", "true");
        return;
      }

      dot.removeAttribute("aria-current");
    });
  }

  findActiveIndex() {
    const viewport = this.slider.vp;
    const scrollPosition = viewport.scrollLeft;
    const maxScroll = viewport.scrollWidth - viewport.clientWidth;

    if (scrollPosition >= maxScroll - 1) {
      return this.snapPositions.length - 1;
    }

    let closestIndex = 0;
    this.snapPositions.forEach((position, index) => {
      const distance = Math.abs(position - scrollPosition);
      const closestDistance = Math.abs(
        this.snapPositions[closestIndex] - scrollPosition,
      );
      if (distance < closestDistance) {
        closestIndex = index;
      }
    });

    return closestIndex;
  }
}

if (!customElements.get("nko-slider-pager")) {
  customElements.define("nko-slider-pager", NkoSliderPager);
}
